import {
  ErrorCode,
  PackageType,
  Purchases,
  PurchasesError,
  type Offering,
  type Package,
} from '@revenuecat/purchases-js'
import { formatCurrency } from './format'
import { track, AnalyticsEvent } from './analytics'
import { Sentry } from './sentry'

/**
 * RevenueCat Web Billing wrapper (purchases-js). Mirrors the Swift
 * `SubscriptionManager`: we render our own paywall and RevenueCat supplies the
 * offering (packages + localized prices) and runs checkout.
 *
 * The App User ID is the Caliu `user.id` — same as iOS — so a web purchase and
 * an app purchase resolve to one entitlement. Degrades to no-op when
 * `VITE_REVENUECAT_WEB_KEY` is unset (paywall falls back to a static price).
 */
const KEY = import.meta.env.VITE_REVENUECAT_WEB_KEY as string | undefined
const ENTITLEMENT_ID = 'pro'

let configuredUser: string | null = null

export function isWebBillingConfigured(): boolean {
  return Boolean(KEY)
}

/**
 * Configure (or re-point) the SDK to the current Caliu user. Safe to call
 * repeatedly. `email`/`name` are attached as reserved subscriber attributes so
 * the user surfaces by email/name in the RevenueCat dashboard, webhooks, and
 * Stripe (matches iOS `SubscriptionManager.logIn`).
 */
export function configurePurchases(
  userId: string,
  email?: string | null,
  name?: string | null,
): void {
  if (!KEY) return
  if (Purchases.isConfigured()) {
    if (configuredUser !== userId) {
      configuredUser = userId
      void Purchases.getSharedInstance()
        .changeUser(userId)
        .then(() => setUserAttributes(email, name))
    } else {
      setUserAttributes(email, name)
    }
    return
  }
  try {
    // purchases-js validates the key format synchronously and throws on a
    // non-Web-Billing key. Without this guard that throw escapes the caller's
    // effect and crashes the whole app. Degrade to the static-price fallback
    // (same as an unset key) and report the misconfiguration.
    Purchases.configure({ apiKey: KEY, appUserId: userId })
    configuredUser = userId
    setUserAttributes(email, name)
  } catch (err) {
    Sentry.captureException(err)
  }
}

/**
 * Sign-out: drop the SDK instance, so nothing (a checkout, the post-checkout
 * entitlement check) keeps running as the account that left.
 */
export function resetPurchases(): void {
  if (Purchases.isConfigured()) Purchases.getSharedInstance().close()
  configuredUser = null
  resetProOfferingCache()
}

/** Attach the user's email / display name as reserved RevenueCat attributes. */
function setUserAttributes(email?: string | null, name?: string | null): void {
  if (!Purchases.isConfigured()) return
  const attrs: Record<string, string | null> = {}
  if (email) attrs.$email = email
  if (name) attrs.$displayName = name
  if (Object.keys(attrs).length === 0) return
  // The web SDK doesn't cache/retry failed attribute writes; report and move on
  // (attributes are best-effort metadata, not required for the entitlement).
  void Purchases.getSharedInstance()
    .setAttributes(attrs)
    .catch((err) => Sentry.captureException(err))
}

// Cache the in-flight/resolved offering so the paywall opens without a "jump":
// the settings menu warms this, the dialog reuses it. Cleared on failure so a
// later open retries.
let offeringPromise: Promise<Offering | null> | null = null

/** The current offering (packages + prices) for the paywall, or null. Cached. */
export function loadProOffering(): Promise<Offering | null> {
  if (!KEY || !Purchases.isConfigured()) return Promise.resolve(null)
  if (!offeringPromise) {
    offeringPromise = Purchases.getSharedInstance()
      .getOfferings()
      .then((o) => o.current ?? null)
      .catch(() => {
        offeringPromise = null // allow a retry on the next open
        return null
      })
  }
  return offeringPromise
}

/**
 * Drop the cached offering so the next `loadProOffering` hits the network.
 * Backs the paywall's "try again" when the offering came back empty.
 */
export function resetProOfferingCache(): void {
  offeringPromise = null
}

/** Warm the offering cache ahead of the paywall (e.g. when the menu opens). */
export function prefetchProOffering(): void {
  void loadProOffering()
}

/**
 * Price for a package formatted in the UI locale (e.g. "24,99 €" in Catalan).
 * RevenueCat's `formattedPrice` ignores the app language, so we format the
 * raw amount + currency ourselves.
 */
export function packagePrice(pkg: Package): string {
  const price = pkg.webBillingProduct.currentPrice
  return formatCurrency(price.amountMicros / 1_000_000, price.currency)
}

/** Plan + price + billing-portal URL for the active Pro subscription. */
export interface ProDetails {
  packageType: PackageType | null
  price: string | null
  /** RevenueCat/Stripe billing portal, when Web Billing exposes one. */
  managementURL: string | null
}

/**
 * Display details for the "thank you" dialog: the active plan's type + price
 * (matched from the offering) and the billing-portal URL. All fields degrade to
 * null when unconfigured, on error, or when RC exposes no portal — the dialog
 * hides whatever is missing.
 */
export async function loadProDetails(): Promise<ProDetails> {
  const empty: ProDetails = { packageType: null, price: null, managementURL: null }
  if (!Purchases.isConfigured()) return empty
  try {
    const info = await Purchases.getSharedInstance().getCustomerInfo()
    const productId = info.entitlements.active[ENTITLEMENT_ID]?.productIdentifier ?? null
    const offering = await loadProOffering()
    const pkg =
      offering?.availablePackages.find(
        (p) => p.webBillingProduct.identifier === productId,
      ) ?? null
    return {
      packageType: pkg?.packageType ?? null,
      price: pkg ? packagePrice(pkg) : null,
      managementURL: info.managementURL ?? null,
    }
  } catch {
    return empty
  }
}

/**
 * Billing cadence for the funnel — `annual` / `monthly` / `lifetime`, else the
 * raw type. `lifetime` is the one-time `caliu_pro_lifetime` purchase.
 */
export function billingPeriod(pkg: Package): string {
  if (pkg.packageType === PackageType.Annual) return 'annual'
  if (pkg.packageType === PackageType.Monthly) return 'monthly'
  if (pkg.packageType === PackageType.Lifetime) return 'lifetime'
  return pkg.packageType
}

/**
 * Offering metadata (set in the RevenueCat dashboard) that every paywall reads,
 * web and Swift alike:
 *
 *   { "lifetime_launch_price": true,
 *     "lifetime_final_price": { "EUR": 69.99, "USD": 64.99 } }
 *
 * `lifetime_launch_price` flags the lifetime plan as being sold at its launch
 * price; only a literal `true` counts. Missing metadata, a string "true", or
 * anything else reads as false: when in doubt, promise nothing.
 */
export function isLifetimeLaunchPrice(offering: Offering | null): boolean {
  return offering?.metadata?.lifetime_launch_price === true
}

/**
 * The lifetime plan's final (post-launch) price for `currency`, formatted in
 * the UI locale, or null when the metadata carries no number for that ISO
 * code. Amounts are keyed by upper-case ISO currency code, as numbers.
 */
export function lifetimeFinalPrice(
  offering: Offering | null,
  currency: string,
): string | null {
  const table = offering?.metadata?.lifetime_final_price
  if (!table || typeof table !== 'object') return null
  const code = currency.toUpperCase()
  const amount = (table as Record<string, unknown>)[code]
  if (typeof amount !== 'number' || !Number.isFinite(amount)) return null
  return formatCurrency(amount, code)
}

// A resolved checkout whose returned `customerInfo` lacks the entitlement is
// almost always propagation lag (seen on first-time trials), not a broken
// purchase. Give RevenueCat this long to catch up before involving Sentry.
const ENTITLEMENT_VERIFY_DELAYS_MS = [2_000, 4_000, 8_000, 16_000]

/**
 * Background check behind the optimistic `true` from `purchaseProPackage`.
 * The stale-`customerInfo` lag self-heals within seconds, so re-fetch fresh
 * customer info a few times (mirrors Swift's post-purchase reconciliation
 * polling in `handlePurchaseCompleted`) and report to Sentry only when a fresh
 * fetch still shows no active entitlement at the end of the window — the
 * genuinely broken checkout (e.g. the product not attached to the "pro"
 * entitlement in the RevenueCat dashboard) that the alert exists to catch.
 */
async function reportIfEntitlementNeverActivates(plan: string, period: string): Promise<void> {
  let sawMissingOnFreshFetch = false
  for (const delayMs of ENTITLEMENT_VERIFY_DELAYS_MS) {
    await new Promise((resolve) => setTimeout(resolve, delayMs))
    if (!Purchases.isConfigured()) return // logged out mid-check
    try {
      const info = await Purchases.getSharedInstance().getCustomerInfo()
      if (info.entitlements.active[ENTITLEMENT_ID] != null) return // lag resolved
      sawMissingOnFreshFetch = true
    } catch {
      // Fetch failed (network) — we learned nothing this round; keep trying.
      // A transport error is not the missing-entitlement signal, so it is not
      // reported as one.
    }
  }
  // Without one fresh fetch confirming the miss, the only evidence is the
  // known-stale purchase response — not enough to raise an alert on.
  if (!sawMissingOnFreshFetch) return
  Sentry.captureException(
    new Error(`RevenueCat checkout resolved without an active "${ENTITLEMENT_ID}" entitlement`),
    {
      extra: {
        plan,
        period,
        verifyWindowMs: ENTITLEMENT_VERIFY_DELAYS_MS.reduce((a, b) => a + b, 0),
      },
    },
  )
}

/**
 * Discount codes (RevenueCat Web Billing "Discounts"). The checkout always
 * shows its Promo code field, and a link to the app can carry a code in
 * `?discount_code=`, the same parameter RevenueCat's purchase links read, so
 * one marketing link works for both. The code is kept in localStorage because
 * the visitor usually signs up first (the verification email opens a new tab)
 * and only reaches checkout later. Whether a code applies (product, expiry,
 * eligibility) is decided by RevenueCat; an invalid one shows its own error.
 */
export const DISCOUNT_CODE_PARAM = 'discount_code'
const DISCOUNT_CODE_KEY = 'caliu-discount-code'

/** The code in a query string, or null. Codes are case-insensitive upstream. */
export function discountCodeFromSearch(search: string): string | null {
  const code = new URLSearchParams(search).get(DISCOUNT_CODE_PARAM)?.trim()
  return code ? code : null
}

function storeDiscountCode(code: string | null): void {
  try {
    if (code) localStorage.setItem(DISCOUNT_CODE_KEY, code)
    else localStorage.removeItem(DISCOUNT_CODE_KEY)
  } catch {
    // Storage blocked (private mode): the checkout field still works.
  }
}

function storedDiscountCode(): string | null {
  try {
    return localStorage.getItem(DISCOUNT_CODE_KEY)
  } catch {
    return null
  }
}

/** Remember a `?discount_code=` from the landing URL. Called once at boot. */
export function captureDiscountCodeFromUrl(): void {
  const code = discountCodeFromSearch(window.location.search)
  if (code) storeDiscountCode(code)
}

/**
 * Run RevenueCat checkout for a package. Returns true when it results in active
 * Pro. Swallows user-cancellation (and other errors) into false — the paywall
 * just stays open.
 *
 * Emits the purchase funnel (`checkout_started` → `checkout_completed` /
 * `checkout_cancelled` / `checkout_failed`). `feature` is the paywall trigger that
 * led here, threaded through so the funnel is attributable. Mirrors Swift's
 * `SubscriptionManager.purchase`.
 */
export async function purchaseProPackage(
  pkg: Package,
  customerEmail?: string | null,
  feature?: string | null,
): Promise<boolean> {
  if (!Purchases.isConfigured()) return false
  const period = billingPeriod(pkg)
  const plan = pkg.webBillingProduct.identifier
  track(AnalyticsEvent.CheckoutStarted, { feature, plan, period })
  try {
    // Prefill the email so RevenueCat's checkout doesn't ask for it — we already
    // know it from the Caliu account. Omitted → checkout prompts as before.
    const { customerInfo } = await Purchases.getSharedInstance().purchase({
      rcPackage: pkg,
      // Null for a Sign in with Apple account that withheld its address —
      // checkout prompts for an email as before.
      customerEmail: customerEmail ?? undefined,
      showDiscountCodeField: true,
      discountCode: storedDiscountCode() ?? undefined,
      // Removing or replacing the code in checkout updates the remembered one.
      onDiscountCodeChanged: storeDiscountCode,
    })
    // The TRANSACTION decides the outcome, never entitlement propagation: a
    // checkout that resolves without throwing went through, but the
    // `customerInfo` it returns can still be stale. Keying on the entitlement
    // dropped every real purchase out of the funnel AND left the payer looking
    // at the paywall. The caller reconciles against `GET /subscription`, which
    // is the authority, so an optimistic true here cannot strand anyone.
    track(AnalyticsEvent.CheckoutCompleted, { plan, period })
    storeDiscountCode(null) // spent
    if (customerInfo.entitlements.active[ENTITLEMENT_ID] == null) {
      // Known propagation lag — verify in the background and alert only if the
      // entitlement never activates. Fire-and-forget: the unlock isn't delayed.
      void reportIfEntitlementNeverActivates(plan, period)
    }
    return true
  } catch (err) {
    // Distinguish an intentional cancel from a real failure for the funnel.
    if (err instanceof PurchasesError && err.errorCode === ErrorCode.UserCancelledError) {
      track(AnalyticsEvent.CheckoutCancelled, { feature })
    } else {
      track(AnalyticsEvent.CheckoutFailed, { feature })
    }
    return false
  }
}
